/* ==========================================================================
   Giorgio cart drawer — the only client-side behaviour the drawer needs.

   Everything the customer READS (reward ladder, gap note, which suggestions to
   show, totals) is rendered in Liquid and comes back fresh with every cart
   mutation through the Section Rendering API. So the only job here is adding a
   suggestion to the bag and handing the response back to Dawn's own drawer.

   Listeners are delegated from the document because the rail is replaced
   wholesale on every cart update.
   ========================================================================== */
(function () {
  'use strict';

  var SECTIONS = ['cart-drawer', 'cart-icon-bubble'];

  // PUB_SUB_EVENTS is a top-level `const` in Dawn's constants.js. That makes it a
  // global binding but NOT a property of window, so it has to be read as a bare
  // identifier — window.PUB_SUB_EVENTS is undefined and every guard on it fails.
  var EVENTS = typeof PUB_SUB_EVENTS !== 'undefined' ? PUB_SUB_EVENTS : null;
  var pub = typeof publish === 'function' ? publish : null;
  var sub = typeof subscribe === 'function' ? subscribe : null;



  /* ---- the reward bar actually moving -----------------------------------
     The fill is rendered server-side with its final width, and every cart
     change replaces the whole drawer — so the browser sees a brand new element
     that has always been that wide. Nothing to transition from, and the bar
     jumps.

     So the last width is remembered here: after each render the new fill is put
     back to where the old one stood, and only then released to its target. The
     CSS transition does the rest.                                            */
  var lastFill = null;

  function animateFill() {
    var fill = document.querySelector('.gio-drawer .rw-fill');
    if (!fill) return;

    var target = fill.style.width;
    if (!target) return;

    if (lastFill === null || lastFill === target) {
      lastFill = target;
      return;
    }

    fill.style.transition = 'none';
    fill.style.width = lastFill;
    // Read it back so the browser commits the start position before the change.
    void fill.offsetWidth;
    fill.style.transition = '';
    fill.style.width = target;
    lastFill = target;
  }

  function drawer() {
    return document.querySelector('cart-drawer');
  }

  // Dawn's own renderContents() swaps #CartDrawer and the header bubble, then
  // re-opens the drawer. Reusing it keeps one render path for every cart change.
  function applyCartResponse(parsed) {
    var el = drawer();
    if (el && typeof el.renderContents === 'function') {
      el.renderContents(parsed);
    } else {
      window.location.reload();
      return;
    }
    if (pub && EVENTS) {
      pub(EVENTS.cartUpdate, {
        source: 'giorgio-cart-rail',
        cartData: parsed,
      });
    }
  }

  function addVariant(button) {
    var variantId = button.getAttribute('data-variant-id');
    if (!variantId) return;

    var label = button.textContent;
    button.setAttribute('aria-busy', 'true');
    button.textContent = 'Adding…';

    var body = {
      items: [{ id: Number(variantId), quantity: 1 }],
      sections: SECTIONS,
      sections_url: window.location.pathname,
    };

    fetch((window.routes && window.routes.cart_add_url ? window.routes.cart_add_url : '/cart/add') + '.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/javascript',
      },
      body: JSON.stringify(body),
    })
      .then(function (response) {
        return response.json();
      })
      .then(function (parsed) {
        if (parsed.status) {
          // Sold out between render and click, or a quantity rule refused it.
          button.removeAttribute('aria-busy');
          button.textContent = parsed.description || parsed.message || 'Unavailable';
          return;
        }
        applyCartResponse(parsed);
      })
      .catch(function () {
        button.removeAttribute('aria-busy');
        button.textContent = label;
      });
  }

  /* ---- the top-reward claim dialog -------------------------------------
     The mockup's #dozenModal in its reward state. The dialog itself is rendered
     server-side with the cart; this drives the picker, the staged list, and the
     commit.

     THE PRICING RULE, from the mockup: the FIRST bag is the reward and the rest
     are ordinary purchases. So a staged row of one reads "$16 Free", a row of
     two reads "$32 $16", and on commit the free unit goes in as its own line
     carrying _gio_reward while the remainder goes in as a plain line. That
     split is what lets a discount zero exactly one bag without having to reason
     about quantities.                                                         */
  var staged = []; // { variantId, name, price, qty }

  function modal() {
    return document.getElementById('GioRewardModal');
  }

  function picker() {
    return document.getElementById('GioRewardPick');
  }

  function money(cents) {
    var n = cents / 100;
    return '$' + (n % 1 === 0 ? n.toFixed(0) : n.toFixed(2));
  }

  function totalBags() {
    return staged.reduce(function (n, s) {
      return n + s.qty;
    }, 0);
  }

  function renderStaged() {
    var m = modal();
    if (!m) return;
    var host = m.querySelector('[data-grm-staged]');
    if (!host) return;

    // Only the very first unit across the whole list is free.
    var seen = 0;
    host.innerHTML = staged
      .map(function (s, i) {
        var freeUnits = seen === 0 ? 1 : 0;
        seen += s.qty;
        var gross = s.price * s.qty;
        var net = s.price * (s.qty - freeUnits);
        var price = freeUnits
          ? '<span class="was">' + money(gross) + '</span>' + (net > 0 ? money(net) : 'Free')
          : money(gross);
        return (
          '<div class="pair-staged-row">' +
          '<div class="pair-staged-info"><div class="pair-staged-name">' +
          s.name +
          '</div></div>' +
          '<div class="pair-staged-controls">' +
          '<div class="pair-staged-controls-top">' +
          '<div class="pair-staged-price">' +
          price +
          '</div>' +
          '<div class="pair-staged-qty">' +
          '<button type="button" data-grm-qty="-1" data-i="' + i + '">&ndash;</button>' +
          '<span>' + s.qty + '</span>' +
          '<button type="button" data-grm-qty="1" data-i="' + i + '">+</button>' +
          '</div></div>' +
          '<button type="button" class="pair-staged-remove" data-grm-unstage data-i="' + i + '">Remove</button>' +
          '</div></div>'
        );
      })
      .join('');

    var label = m.querySelector('[data-grm-label]');
    if (label) {
      label.textContent = staged.length
        ? label.dataset.more || 'Add another coffee or tea'
        : label.dataset.placeholder || label.textContent;
    }

    var p = picker();
    if (p) p.classList.toggle('chosen', staged.length > 0);

    // The copy stops asking once the reward is settled, and says plainly what
    // happens to anything beyond the first bag.
    var status = m.querySelector('[data-grm-status]');
    if (status) {
      var extra = totalBags() - 1;
      if (!staged.length) {
        status.innerHTML = status.dataset.help || '';
      } else if (extra <= 0) {
        status.innerHTML = 'Yours free &mdash; added at checkout.';
      } else {
        status.innerHTML =
          'Your first bag is free. ' +
          (extra === 1 ? 'The second is' : 'The others are') +
          ' added at full price.';
      }
    }

    var cta = m.querySelector('[data-grm-add]');
    if (cta) {
      cta.disabled = staged.length === 0;
      cta.textContent = staged.length ? 'Add my free bag' : 'Add to Bag';
    }
  }

  function openModal() {
    var m = modal();
    if (!m) return;
    var status = m.querySelector('[data-grm-status]');
    if (status && !status.dataset.help) status.dataset.help = status.innerHTML;
    m.hidden = false;
    renderStaged();
    var trigger = m.querySelector('[data-grm-toggle]');
    if (trigger) trigger.focus();
  }

  function closeModal() {
    var m = modal();
    if (!m) return;
    m.hidden = true;
    staged = [];
    var p = picker();
    if (p) p.classList.remove('open');
    renderStaged();
  }

  function toggleMenu() {
    var p = picker();
    if (!p) return;
    var open = p.classList.toggle('open');
    var trigger = p.querySelector('[data-grm-toggle]');
    if (trigger) trigger.setAttribute('aria-expanded', open ? 'true' : 'false');
  }

  function stage(option) {
    var id = option.getAttribute('data-variant-id');
    var existing = staged.filter(function (s) {
      return s.variantId === id;
    })[0];
    if (existing) {
      existing.qty += 1;
    } else {
      staged.push({
        variantId: id,
        name: option.getAttribute('data-name') || '',
        price: Number(option.getAttribute('data-price')) || 0,
        qty: 1,
      });
    }
    var p = picker();
    if (p) {
      p.classList.remove('open');
      var trigger = p.querySelector('[data-grm-toggle]');
      if (trigger) trigger.setAttribute('aria-expanded', 'false');
    }
    renderStaged();
  }

  function stagedQty(index, delta) {
    var row = staged[index];
    if (!row) return;
    row.qty += delta;
    if (row.qty < 1) staged.splice(index, 1);
    renderStaged();
  }

  function unstage(index) {
    staged.splice(index, 1);
    renderStaged();
  }

  function addReward(cta) {
    if (!staged.length) return;

    cta.disabled = true;
    cta.textContent = 'Adding…';

    /* The mockup commits the reward as exactly this:

           order.push({ ..., price: 0, qty: 1, isReward: true })

       One line, one bag, no charge. Anything beyond that first bag is an
       ordinary purchase, which is why the dialog says "Your first bag is free.
       The second is added at full price." So the free bag goes in on its own
       line carrying the marker, and the remainder goes in plain — Shopify then
       merges that remainder with any coffee already in the bag, instead of
       leaving a second mystery line beside it. */
    var items = [];
    staged.forEach(function (s, i) {
      if (i === 0) {
        items.push({ id: Number(s.variantId), quantity: 1, properties: { _gio_reward: 'top-tier' } });
        if (s.qty > 1) items.push({ id: Number(s.variantId), quantity: s.qty - 1 });
      } else {
        items.push({ id: Number(s.variantId), quantity: s.qty });
      }
    });

    fetch((window.routes && window.routes.cart_add_url ? window.routes.cart_add_url : '/cart/add') + '.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/javascript' },
      body: JSON.stringify({ items: items, sections: SECTIONS, sections_url: window.location.pathname }),
    })
      .then(function (response) {
        return response.json();
      })
      .then(function (parsed) {
        if (parsed.status) {
          cta.disabled = false;
          cta.textContent = parsed.description || parsed.message || 'Unavailable';
          return;
        }
        closeModal();
        applyCartResponse(parsed);
      })
      .catch(function () {
        cta.disabled = false;
        cta.textContent = 'Add my free bag';
      });
  }

  document.addEventListener('click', function (event) {
    var toggle = event.target.closest('[data-gio-rail-toggle]');
    if (toggle) {
      var item = toggle.closest('.ur-item');
      var list = item && item.querySelector('.ur-variants');
      if (list) {
        var open = list.classList.toggle('open');
        toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
      }
      return;
    }

    var add = event.target.closest('[data-gio-rail-add]');
    if (add && !add.disabled) {
      event.preventDefault();
      addVariant(add);
      return;
    }

    if (event.target.closest('[data-grm-open]')) {
      event.preventDefault();
      openModal();
      return;
    }

    // The scrim and "No, thank you." are the same answer as the close mark.
    if (event.target.closest('[data-grm-close]')) {
      event.preventDefault();
      closeModal();
      return;
    }

    // Clicking the ground outside the panel is the same answer as the close
    // mark, whether it lands on the scrim or on the dialog's own padding.
    if (event.target === document.getElementById('GioRewardModal')) {
      event.preventDefault();
      closeModal();
      return;
    }

    if (event.target.closest('[data-grm-toggle]')) {
      event.preventDefault();
      toggleMenu();
      return;
    }

    var option = event.target.closest('[data-grm-pick]');
    if (option) {
      event.preventDefault();
      stage(option);
      return;
    }

    var step = event.target.closest('[data-grm-qty]');
    if (step) {
      event.preventDefault();
      stagedQty(Number(step.getAttribute('data-i')), Number(step.getAttribute('data-grm-qty')));
      return;
    }

    var drop = event.target.closest('[data-grm-unstage]');
    if (drop) {
      event.preventDefault();
      unstage(Number(drop.getAttribute('data-i')));
      return;
    }

    var commit = event.target.closest('[data-grm-add]');
    if (commit && !commit.disabled) {
      event.preventDefault();
      addReward(commit);
      return;
    }

    // Clicking anywhere else shuts the menu, the way every other dropdown here does.
    var p = picker();
    if (p && p.classList.contains('open') && !p.contains(event.target)) {
      p.classList.remove('open');
    }
  });

  if (sub && EVENTS) {
    sub(EVENTS.cartUpdate, function () {
      // After the section swap, not during it.
      setTimeout(animateFill, 0);
    });
  }
  document.addEventListener('DOMContentLoaded', animateFill);

  document.addEventListener('keydown', function (event) {
    var m = modal();
    if (event.key === 'Escape' && m && !m.hidden) {
      event.stopPropagation();
      closeModal();
    }
  });
})();
